<template>
  <section class="section" id="products-panel">
    <div
      class="container"
      :style="hasChangesToSave ? 'padding-bottom: 3rem' : ''"
    >
      <b-loading
        :is-full-page="true"
        :active="isLoading || isLoadingScaleProducts || isLoadingPurchases"
        :can-cancel="false"
      ></b-loading>

      <store-header :storeBeingManaged="storeBeingManaged" />

      <products-title
        :storeBeingManaged="storeBeingManaged"
        :productsQty="products.length"
        :scaleProductsQty="scaleProducts.length"
        @get-products="confirmGetProducts"
      />

      <b-tabs
        position="is-centered"
        class="block"
        type="is-boxed"
        expanded
        @input="changeTabsStyle"
      >
        <b-tab-item
          :label="`${products.length} ${$t('products.industrialized')}`"
        >
          <div v-if="permissions.addProductToCatalogUpdatePrice">
            <p class="explanation has-text-centered">
              {{ $t("products.industrializedProductsInstructionsNote1") }}
              <br />
              {{ $t("products.industrializedProductsInstructionsNote2") }}
            </p>

            <b-field class="barcode-search">
              <b-input
                v-model="barcode"
                :placeholder="$t('products.code')"
                type="tel"
                icon="barcode"
                @input.native="formatOnlyDigits('barcode')"
              ></b-input>

              <p class="control">
                <button
                  class="button"
                  :style="{
                    backgroundColor: $primaryColor,
                    color: $primaryColorContrast
                  }"
                  @click="searchProduct"
                >
                  {{ $t("products.import") }}
                </button>
              </p>
            </b-field>
          </div>

          <div v-if="permissions.bulkAddProductToCatalogUpdatePrice">
            <p class="explanation has-text-centered">
              {{ $t("products.bulkUpdateNote1") }}
              <a
                :href="
                  locale === 'en' || address.country === 'United Arab Emirates'
                    ? 'https://s3.amazonaws.com/assets.onii.com.br/models/en/Onii-Template_upload_prices_products.xlsx'
                    : 'https://s3.amazonaws.com/assets.onii.com.br/models/pt/Onii-Modelo_upload_precos_produtos.xlsx'
                "
                target="_blank"
              >
                {{ $t("products.thisSpreadsheetModel") }}
              </a>
              {{ $t("products.bulkUpdateNote2") }}
            </p>

            <div class="explanation has-text-centered">
              <b-tooltip
                :label="$t('products.processingFile')"
                v-if="processingProducts"
              >
                <button class="button" :disabled="true">
                  <b-icon icon="file-upload"></b-icon>
                  &nbsp;&nbsp;&nbsp;{{ $t("products.pricesSpreadsheet") }}
                </button>
              </b-tooltip>

              <button
                class="button"
                @click="$refs.bulkUpdateProducts.click()"
                v-else
              >
                <b-icon icon="file-upload"></b-icon>
                &nbsp;&nbsp;&nbsp;{{ $t("products.pricesSpreadsheet") }}
              </button>
            </div>
          </div>
          <br />

          <input
            type="file"
            ref="bulkUpdateProducts"
            id="bulk-update-products"
            style="display: none"
            accept=".xls,.xlsx"
            @change="bulkUpdateProducts"
          />

          <h2 v-if="products.length === 0" class="subtitle has-text-centered">
            {{ $t("products.noProductsRegisteredAtThisMoment") }}
          </h2>

          <div v-else>
            <stock-value
              :products="productsWithCosts"
              v-if="permissions.seeStockValueSummary"
            />

            <div
              class="columns is-gapless is-vcentered"
              v-if="permissions.updateCriticalStockLevel"
            >
              <div class="column is-narrow">
                {{ $t("products.criticalStockLevel") }}:
              </div>
              <div class="column">
                <b-input
                  type="tel"
                  v-model="criticalStockLevel"
                  style="width: 60px; margin-left: 5px;"
                  @input="hasChangesToSave = true"
                ></b-input>
              </div>
            </div>
            <!-- <stock-operations :storeType="storeBeingManaged.storeInfo.type" /> -->
            <products-table
              :products="productsWithCosts"
              :storeBeingManaged="storeBeingManaged"
              :criticalStockLevel="criticalStockLevel"
              :saving-cost-barcode="savingCostBarcode"
              @has-changes-to-save="hasChangesToSave = true"
              @update-cost="savePurchase"
              @update-price="updatePrice"
              @update-vip-price="updateVipPrice"
              @update-ideal-qty="updateIdealQty"
              @update-current-qty="updateCurrentQty"
              @update-validity="updateValidity"
              @update-vat="updateVAT"
            />
          </div>
        </b-tab-item>

        <b-tab-item
          :label="`${scaleProducts.length} ${$t('products.weighted')}`"
          id="tab"
        >
          <p class="explanation has-text-centered">
            {{ $t("scaleProducts.scaleProductsInstructionsNote1") }}
            <br />
            {{ $t("scaleProducts.scaleProductsInstructionsNote2") }}
          </p>

          <scale-products-table
            :storeBeingManaged="storeBeingManaged"
            :scaleProducts="scaleProducts"
            @update-scale-product-price="updateScaleProductPrice"
          />
        </b-tab-item>
      </b-tabs>
    </div>

    <div
      v-if="hasChangesToSave"
      class="has-text-centered"
      style="position: fixed; bottom: 0; left: 0; width: 100%; background-color: white; margin-left: -1rem; border-top: 1px solid black;"
    >
      <button
        class="button"
        :style="{
          backgroundColor: $primaryColor,
          color: $primaryColorContrast
        }"
        style="margin: 1rem"
        @click="saveChanges"
      >
        {{ $t("products.saveChanges") }}
      </button>
    </div>

    <b-modal :active.sync="isConfirmProductModalOpen">
      <confirm-product-modal
        :product="product"
        :storeId="storeBeingManaged._id"
        @save-product="saveProduct"
      />
    </b-modal>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";
import { mask } from "../../mixins/mask";
import storeHeader from "../common/StoreHeader";
import productsTitle from "./ProductsTitle";
import productsTable from "./ProductsTable";
import scaleProductsTable from "./ScaleProductsTable";
import confirmProductModal from "./ConfirmProductModal";
import stockValue from "./stock/StockValue";

export default {
  mixins: [common, mask],

  components: {
    storeHeader,
    productsTitle,
    productsTable,
    scaleProductsTable,
    confirmProductModal,
    stockValue
  },

  data() {
    return {
      isLoading: false,
      isLoadingScaleProducts: false,
      isLoadingPurchases: false,
      isConfirmProductModalOpen: false,
      barcode: "",
      product: "",
      processingProducts: true,
      hasChangesToSave: false,
      savingCostBarcode: "",
      priceChanges: [],
      vipPriceChanges: [],
      idealQuantityChanges: [],
      currentQuantityChanges: [],
      validityChanges: [],
      vatChanges: [],
      productsToExclude: [],
      criticalStockLevel: 10
    };
  },

  computed: {
    ...mapGetters([
      "storeBeingManaged",
      "products",
      "scaleProducts",
      "purchases",
      "permissions",
      "locale",
      "address"
    ]),

    purchasesArray() {
      let purchasesArray = [];
      for (let i = 0; i < this.purchases.length; i++) {
        purchasesArray[this.purchases[i].b] = this.purchases[i].lc;
      }
      return purchasesArray;
    },

    productsWithCosts() {
      return this.products.map(p => {
        return this.purchasesArray[p.b] && this.purchasesArray[p.b] > 0
          ? {
              ...p,
              rq: p.iq - p.cq,
              lc: this.purchasesArray[p.b],
              m: (p.p - this.purchasesArray[p.b]) / p.p
            }
          : {
              ...p,
              rq: p.iq - p.cq,
              lc: 0
            };
      });
    }
  },

  methods: {
    confirmGetProducts() {
      if (this.hasChangesToSave) {
        this.$buefy.dialog.confirm({
          message: this.$t("products.updatingWillLooseChanges"),
          confirmText: this.$t("products.okUpdate"),
          cancelText: this.$t("common.cancel"),
          type: "is-primary",
          onConfirm: () => this.getProducts()
        });
      } else {
        this.getProducts();
      }
    },

    getProducts() {
      this.isLoading = true;
      this.$store
        .dispatch("getProducts", this.storeBeingManaged._id)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.processingProducts = res.data.payload.processingProducts;
            this.hasChangesToSave = false;
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getPurchases() {
      this.isLoadingPurchases = true;
      this.$store
        .dispatch("getAllPurchases")
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoadingPurchases = false;
        });
    },

    saveProduct(data) {
      this.isLoading = true;
      this.$store
        .dispatch("saveProduct", data)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.isConfirmProductModalOpen = false;
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    savePurchase(product) {
      this.savingCostBarcode = product.product.b;
      this.$store
        .dispatch("savePurchase", product)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$buefy.toast.open({
              message: this.$t("products.changesSaved"),
              type: "is-success",
              position: "is-bottom",
              duration: 3000
            });
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.savingCostBarcode = "";
        });
    },

    updateScaleProductPrice(data) {
      this.isLoading = true;
      this.$store
        .dispatch("updateScaleProductPrice", data)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    updatePrice(product) {
      let index;

      if (product.p > 0) {
        // put product in price changes array
        index = this.priceChanges.findIndex(c => {
          return c.b === product.b;
        });
        if (index > -1) {
          this.priceChanges[index].p = product.p;
        } else {
          this.priceChanges.push(product);
        }

        // remove product from products to exclude array
        index = this.productsToExclude.findIndex(c => {
          return c.b === product.b;
        });
        if (index > -1) {
          this.productsToExclude.splice(index, 1);
        }
      } else if (product.p === 0) {
        // put product in products to exclude array
        index = this.productsToExclude.findIndex(c => {
          return c.b === product.b;
        });
        if (index > -1) {
          this.productsToExclude[index].p = product.p;
        } else {
          this.productsToExclude.push(product);
        }

        // remove product from price changes array
        index = this.priceChanges.findIndex(c => {
          return c.b === product.b;
        });
        if (index > -1) {
          this.priceChanges.splice(index, 1);
        }

        // this.$store.dispatch("removeProductFromCatalog", product.b);
      }
    },

    updateVipPrice(product) {
      let index;

      // put product in price changes array
      index = this.vipPriceChanges.findIndex(c => {
        return c.b === product.b;
      });
      if (index > -1) {
        this.vipPriceChanges[index].vp = product.vp;
      } else {
        this.vipPriceChanges.push(product);
      }

      this.hasChangesToSave = true;
    },

    updateIdealQty(product) {
      const index = this.idealQuantityChanges.findIndex(c => {
        return c.b === product.b;
      });
      if (index > -1) {
        this.idealQuantityChanges[index].iq = product.iq;
      } else {
        this.idealQuantityChanges.push(product);
      }
    },

    updateCurrentQty(product) {
      const index = this.currentQuantityChanges.findIndex(c => {
        return c.b === product.b;
      });
      if (index > -1) {
        this.currentQuantityChanges[index].cq = product.cq;
      } else {
        this.currentQuantityChanges.push(product);
      }
    },

    updateValidity(product) {
      const index = this.validityChanges.findIndex(c => {
        return c.b === product.b;
      });
      if (index > -1) {
        this.validityChanges[index].v = product.v;
      } else {
        this.validityChanges.push(product);
      }
    },

    updateVAT(product) {
      const index = this.vatChanges.findIndex(c => {
        return c.b === product.b;
      });
      if (index > -1) {
        this.vatChanges[index].vat = product.vat;
      } else {
        this.vatChanges.push(product);
      }
    },

    searchProduct() {
      if (!/\d+|^CB+\d+$/.test(this.barcode)) {
        this.alertError({ message: "INVALID_BARCODE" });
        return;
      }
      this.isLoading = true;
      this.$store
        .dispatch("searchProduct", { barcode: this.barcode })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.isConfirmProductModalOpen = true;
            this.product = res.data.payload.product;
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    saveChanges() {
      this.isLoading = true;

      this.$store.dispatch("updateStoreConfig", {
        storeId: this.storeBeingManaged._id,
        configs: {
          criticalStockLevel: this.criticalStockLevel
        }
      });

      this.$store
        .dispatch("saveProductsChanges", {
          priceChanges: this.priceChanges,
          vipPriceChanges: this.vipPriceChanges,
          idealQuantityChanges: this.idealQuantityChanges,
          currentQuantityChanges: this.currentQuantityChanges,
          validityChanges: this.validityChanges,
          vatChanges: this.vatChanges,
          productsToExclude: this.productsToExclude,
          storeId: this.storeBeingManaged._id
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$buefy.toast.open({
              message: this.$t("products.changesSaved"),
              type: "is-success",
              position: "is-bottom",
              duration: 5000
            });
            this.priceChanges = [];
            this.vipPriceChanges = [];
            this.idealQuantityChanges = [];
            this.currentQuantityChanges = [];
            this.validityChanges = [];
            this.vatChanges = [];
            this.productsToExclude = [];
            this.hasChangesToSave = false;
            this.getProducts();
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    bulkUpdateProducts(event) {
      const file = this.getValidSpreadsheetFile(event);

      if (!file) return;

      const formData = new FormData();

      formData.append("file", file);
      formData.append("storeId", this.storeBeingManaged._id);

      this.isLoading = true;
      this.$store
        .dispatch("bulkUpdateProducts", formData)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$buefy.dialog.alert({
              title: this.$t("common.success"),
              message: `${this.$t("common.waitProcessingTimeNote1")} ${res.data
                .payload.minutes || 2} ${this.$t(
                "common.waitProcessingTimeNote2"
              )}`,
              type: "is-success",
              hasIcon: true,
              icon: "check",
              iconPack: "fa"
            });
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    changeTabsStyle() {
      const tab1 = document.getElementsByClassName("tabs")[0].children[0]
        .children[0];
      const tab2 = document.getElementsByClassName("tabs")[0].children[0]
        .children[1];

      if (tab1.classList.contains("is-active")) {
        tab1.children[0].style.setProperty(
          "border-bottom-color",
          "rgb(219, 219, 219)",
          "important"
        );
        tab2.children[0].style.setProperty(
          "border-bottom-color",
          "transparent",
          "important"
        );
        tab1.children[0].style.setProperty("color", "#4A4A4A", "important");
        tab2.children[0].style.setProperty(
          "color",
          this.$primaryColor,
          "important"
        );
      } else {
        tab1.children[0].style.setProperty(
          "border-bottom-color",
          "transparent",
          "important"
        );
        tab2.children[0].style.setProperty(
          "border-bottom-color",
          "rgb(219, 219, 219)",
          "important"
        );
        tab1.children[0].style.setProperty(
          "color",
          this.$primaryColor,
          "important"
        );
        tab2.children[0].style.setProperty("color", "#4A4A4A", "important");
      }
    },

    getCategories() {
      this.isLoading = true;
      this.$store
        .dispatch("getCategories")
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  },

  mounted() {
    this.getCategories();
    this.getProducts();
    this.getPurchases();

    this.criticalStockLevel = this.storeBeingManaged.configs.criticalStockLevel;

    // set tabs background color
    document.getElementsByClassName(
      "tabs"
    )[0].children[0].style.backgroundColor = "hsl(0, 0%, 96%)";

    this.$nextTick(() => {
      const tab1 = document.getElementsByClassName("tabs")[0].children[0]
        .children[0];
      tab1.children[0].style.setProperty(
        "background-color",
        "hsl(0, 0%, 96%)",
        "important"
      );
      tab1.children[0].style.setProperty(
        "color",
        this.$primaryColor,
        "important"
      );

      const tab2 = document.getElementsByClassName("tabs")[0].children[0]
        .children[1];
      tab2.children[0].style.setProperty(
        "background-color",
        "hsl(0, 0%, 96%)",
        "important"
      );
      tab2.children[0].style.setProperty(
        "border-bottom-color",
        "rgb(219, 219, 219)",
        "important"
      );
      tab2.children[0].style.setProperty("color", "#4A4A4A", "important");
    });
  }
};
</script>

<style scoped>
.explanation {
  /* max-width: 800px; */
  margin-left: auto;
  margin-right: auto;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

#products-panel {
  margin-top: 3rem;
  min-height: 94vh;
  padding: 2rem 1rem;
  background-color: hsl(0, 0%, 96%);
}

.barcode-search {
  max-width: 320px;
  margin: auto;
  margin-top: 1rem;
  margin-bottom: 1rem;
}
</style>
